import {
	FilterType,
	GroupOperator,
	ItemType
} from '@bimser/common/dist/components/ExtremeGrid/entities';
import { BCNotification, IIconMatchersOptions, findMessage } from '@bimser/components';
import { FormItemTypes, IDictionary, IItem } from '@bimser/core';
import { Map } from 'immutable';
import { Liquid } from 'liquidjs';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _unionBy from 'lodash/unionBy';
import { JSX } from 'react';
import {
	DataSourceType,
	IDataSource,
	IOnLoadArgs,
	IPropertyField,
	ISelectableListItem,
	MultiLanguageText,
	PrimitiveType
} from '../../../entities/common';
import { IDataSourceResponse } from '../../../entities/common/DataSource/IDataSourceResponse';
import { convertTypeToFieldType } from '../../../entities/common/IPropertyField';
import { getDataSourceData } from '../../../helper';
import { IDataLoadEventArgs } from '../entities/EventArgs';
import { IListControlProperties } from '../entities/IListControlProperties';
import EditControlBase from './EditControlBase';
import { HTTPRequestResponse } from '@bimser/common';

const liquidEngine = new Liquid();

const supportedMultiSelectControlTypes: Set<FormItemTypes.AllTypes> = new Set([
	FormItemTypes.EControlTypes.TagBox,
	FormItemTypes.EControlTypes.Transfer,
	FormItemTypes.EControlTypes.ListBox
]);

export default class ListControl extends EditControlBase<any> {
	items: ISelectableListItem<MultiLanguageText>[];
	__itemsCollection: IDictionary<ISelectableListItem<MultiLanguageText>>;
	selectedItemValues: any[];
	data: IDictionary<any>[];
	dataSource: IDataSource;
	valueType: PrimitiveType;
	dataSourceType: DataSourceType;
	__dataSourceLoadedAtLeastOnce?: boolean;
	iconExpr?: string;
	iconMatchers?: IIconMatchersOptions[];
	dataCount: number = 0;

	__searchValue: string = '';

	constructor() {
		super();
		this.items = [];
		this.__itemsCollection = {};
		this.selectedItemValues = [];
		this.dataSourceType = 'dynamic';
		this.valueType = 'string';
		this.dataSource = {
			name: '',
			valueExpr: '',
			displayExpr: [],
			displayFormat: '',
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.iconExpr = '';
		this.iconMatchers = [];

		this.__onSearch = this.__onSearch.bind(this);
		this.__onDebounceSearch = _debounce(this.__onDebounceSearch.bind(this), 300);

		this.__excludeChangedProperties = ['__itemsCollection'];
	}

	__getMLClassPath() {
		return [
			...super.__getMLClassPath(),
			'items.__array__.text',
			'__itemsCollection.__array__.text'
		];
	}

	__getReloadFilters() {
		if (this.__searchValue) {
			const displayExpr = this.dataSource.enableMultiLanguageDisplayExpr
				? this.dataSource.displayExprML?.[this.__form.currentLanguage.culture]?.displayExpr
				: this.dataSource.displayExpr;

			if (displayExpr.length === 1 && displayExpr[0] === this.dataSource.valueExpr) {
				switch (this.valueType) {
					case 'integer': {
						const value = Number.parseInt(this.__searchValue);

						if (!Number.isNaN(value)) {
							return [
								{
									Filters: [
										{
											Column: this.dataSource.valueExpr,
											Not: false,
											Type: FilterType.Equal,
											Value: value,
											ItemType: ItemType.Int32
										}
									],
									Operator: GroupOperator.Or
								}
							];
						}

						return null;
					}
					case 'decimal': {
						const value = Number.parseFloat(this.__searchValue);

						if (!Number.isNaN(value)) {
							return [
								{
									Filters: [
										{
											Column: this.dataSource.valueExpr,
											Not: false,
											Type: FilterType.Equal,
											Value: value,
											ItemType: ItemType.Decimal
										}
									],
									Operator: GroupOperator.Or
								}
							];
						}

						return null;
					}
				}
			}

			return [
				{
					Filters: displayExpr.map(exp => ({
						Column: exp,
						Not: false,
						Type: FilterType.Contain,
						Value: this.__searchValue,
						ItemType: ItemType.String
					})),
					Operator: GroupOperator.Or
				}
			];
		}

		return [];
	}

	__getSelectedValues() {
		return Object.keys(this.__itemsCollection).filter(
			value => this.__itemsCollection[value].selected
		);
	}

	get selectedValues(): Array<any> {
		return this.__getSelectedValues();
	}

	__sortItems(items: IItem[]) {
		return items;
	}

	__onLoad({
		externalData,
		reload,
		forceRefresh,
		parameterChanged,
		requiredError,
		ignoreRunAtServer
	}: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		const hasDisplayExpr = this.dataSource.enableMultiLanguageDisplayExpr
			? this.dataSource.displayExprML?.[this.__form.currentLanguage.culture]?.displayExpr
			: this.dataSource.displayExpr;

		if (
			this.__form.serviceUrl &&
			this.dataSourceType === 'dynamic' &&
			this.dataSource.valueExpr &&
			hasDisplayExpr &&
			(!this.dataSource.runAtServer || ignoreRunAtServer)
		) {
			const filters = this.__getReloadFilters();

			if (!filters) {
				return;
			}

			this.__proxyChanging.loading = true;

			getDataSourceData({
				id: this.controlId,
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				loadOptions: {
					pagination: {
						skip: 0,
						take: 100
					},
					sorts: null,
					filters,
					distinct: true,
					filterNulls: true,
					valueExpr: this.dataSource.valueExpr
				},
				forceRefresh,
				requiredError,
				projectVersion: this.__form.packageVersion
			})
				.then(response => this.__onDataSourceSuccess(response, reload, parameterChanged))
				.catch(e => {
					if (
						this.dataSource.parameters?.length &&
						reload &&
						parameterChanged &&
						e?.message !== 'cancel'
					) {
						this.__proxyChanging.items = [];
						this.__proxyChanging.data = [];
						this.__itemsCollection = {};
						this.dataCount = 0;

						this.__form.applyChanges().then(() => {
							this.__proxyChanging.value = null;
							this.__proxyChanging.text = null;
							this.__proxyChanging.selectedItemValues = [];
						});
					}
				})
				.finally(() => {
					setTimeout(() => {
						this.__proxyChanging.loading = false;
					}, 0);
				});
		}

		if (!this.dataSource.name) {
			const selectedItemValues = this.items
				.filter(i => i.selected && i.value)
				.map(i => i.value);
			const isEqual = _isEqual(
				[...this.selectedItemValues].sort(),
				[...selectedItemValues].sort()
			);

			if (!isEqual) {
				this.__proxyChanging.selectedItemValues = [...new Set(selectedItemValues)];
			}
		}
	}

	addItem(args: ISelectableListItem<IDictionary<string>>) {
		const { key, value, icon, selected, text } = args;

		if (this.items.find(item => item.key === key)) {
			BCNotification.warning({
				message: findMessage.get('102412')
			});

			return;
		}

		const multiLanguageText = new MultiLanguageText();
		multiLanguageText.setData(text);

		const newItem = {
			key,
			value,
			icon,
			selected,
			text: multiLanguageText
		};

		if (!selected || supportedMultiSelectControlTypes.has(this.controlType)) {
			this.__proxyChanging.items = [...this.items, newItem];
		} else {
			this.__proxyChanging.items = [
				...this.items.map(item => ({
					...item,
					selected: false
				})),
				newItem
			];
		}
	}

	private __onDataSourceSuccess(
		response: HTTPRequestResponse<IDataSourceResponse>,
		reload: boolean,
		parameterChanged: boolean
	) {
		const dataCount = response.data?.result?.length;
		const dataSourceParameterCount = this.dataSource?.parameters?.length;

		if (dataCount) {
			this.loadDataSourceItems(response, parameterChanged);
		} else {
			if ((dataSourceParameterCount || this.__searchValue) && reload) {
				this.__proxyChanging.items = [];
				this.dataCount = 0;
				this.__proxyChanging.data = [];
				this.__itemsCollection = {};
			}

			this.__proxyChanging.loading = false;
		}
	}

	private loadDataSourceItems(
		response: HTTPRequestResponse<IDataSourceResponse>,
		parameterChanged: boolean
	) {
		this.__generateListControlItems(
			response.data,
			this.dataSource.displayFormat,
			this.dataSource.valueExpr,
			parameterChanged
		).then(listItemsResponse => {
			const newItems = [...listItemsResponse.items];

			if (!parameterChanged) {
				listItemsResponse.unlistedItems.forEach(i => {
					if (listItemsResponse.items.every(item => item.value !== i.value)) {
						newItems.push(i);
					}
				});
			}

			const selectedItems = newItems?.filter(i => i.selected);
			if (selectedItems?.length) {
				this.__proxyChanging.selectedItemValues = [
					...new Set([...this.selectedItemValues, ...selectedItems.map(i => i.value)])
				];
			}

			const tId = this.__form.beginTransaction();

			this.__proxyChanging.data = response.data.result;
			this.__proxyChanging.items = newItems;
			this.dataCount = response.data.count;

			this.__form.endTransaction(tId).then(() => {
				this.__proxyChanging.loading = false;
				this.__dataSourceLoadedAtLeastOnce = true;
				this.emit('event:OnDataLoad', { data: response.data.result } as IDataLoadEventArgs);
			});
		});
	}

	__getDefaultValue() {
		return undefined;
	}

	private __generateListControlItems(
		data: IDataSourceResponse,
		displayFormat: string,
		valueExpr: string,
		parameterChanged: boolean
	) {
		return new Promise<{
			items: ISelectableListItem<MultiLanguageText>[];
			unlistedItems: ISelectableListItem<MultiLanguageText>[];
		}>(resolve => {
			let unlistedItems: ISelectableListItem<MultiLanguageText>[] = [];
			const items: ISelectableListItem<MultiLanguageText>[] = data.result.map(
				(item: any, index) => {
					let selected = false;
					const value = item[valueExpr];

					if (!value) {
						console.warn(
							`${this.name} cannot have an empty Value Expression for ${valueExpr}.`
						);
					}

					if (this.items && !parameterChanged) {
						const itemFound = this.items.find(i => i.value === value);
						const itemAlreadySelected = this.selectedItemValues?.includes(value);

						let defaultValue;

						if (!this.__dataSourceLoadedAtLeastOnce) {
							defaultValue = this.__getDefaultValue();
						}

						if (defaultValue === value) {
							selected = true;
						}
						if (itemFound) {
							selected = itemFound.selected;
						}
						if (itemAlreadySelected) {
							selected = true;
						}

						unlistedItems = this.items.filter(i => i.selected);
					} else if (this.items) {
						const itemFound = this.items.find(i => i.value === value);
						if (itemFound) {
							selected = itemFound.selected;
						}
					}

					const textML = new MultiLanguageText();

					if (
						this.dataSource.enableMultiLanguageDisplayExpr &&
						this.dataSource.displayExprML?.[this.__form.currentLanguage.culture]
							?.displayFormat
					) {
						displayFormat =
							this.dataSource.displayExprML[this.__form.currentLanguage.culture]
								.displayFormat;
						textML.setText(liquidEngine.parseAndRenderSync(displayFormat, item), item);
					} else {
						displayFormat = displayFormat.replace(
							'.__userCulture',
							`['${this.__form.currentLanguage.culture}']`
						);
						textML.setText(liquidEngine.parseAndRenderSync(displayFormat, item));
					}

					const _value = _get(item, this.iconExpr);

					const resultItem: ISelectableListItem<MultiLanguageText> = {
						value,
						text: textML,
						key: index + '_' + value?.toString(),
						icon: this.iconMatchers?.find(matcher => matcher.value == _value)?.icon,
						selected
					};

					if (parameterChanged) {
						this.__itemsCollection = {};
						this.selectedItemValues = [];
					} else {
						if (!this.__itemsCollection[value]) {
							this.__itemsCollection[value] = { ...resultItem };
						}
					}

					return resultItem;
				}
			);

			resolve({ items, unlistedItems });
		});
	}

	__onSearch(value: string) {
		if (this.__searchValue !== value) {
			this.__searchValue = value;
			this.__onDebounceSearch();
		}
	}

	__onDebounceSearch() {
		this.__dataSourceValueEntryFlag = false;
		this.reload(false, true, true);
	}

	reload(forceRefresh = false, ignoreRunAtServer = false, byPass = false) {
		if (this.dataSource?.name && (!this.dataSource.runAtServer || ignoreRunAtServer)) {
			if (this.dataSource?.parameters.length) {
				this.__proxyChanging.loading = true;
				this.__dataSourceValueEntryFlag = false;
				this.__reloadDataSource(this.dataSource, forceRefresh, false, byPass).finally(
					() => {
						this.__proxyChanging.loading = false;
					}
				);
			} else {
				this.__onLoad({ reload: true, forceRefresh, ignoreRunAtServer });
			}
		}
	}

	changeDataSource(data: any) {
		this.__proxyChanging.items = [];
		this.dataCount = 0;
		this.__proxyChanging.data = [];
		this.__itemsCollection = {};
		this.__proxyChanging.selectedItemValues = [];
		this.__proxyChanging.dataSource = data;
	}

	__loadProperties(data: IListControlProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.valueType = data.valueType;
		this.__proxyChanged.dataSourceType = data.dataSourceType;
		this.__proxyChanged.data = data.data;
		this.__proxyChanged.iconExpr = data.iconExpr;
		this.__proxyChanged.iconMatchers = data.iconMatchers;

		if (data.items?.length) {
			const newItems: ISelectableListItem<MultiLanguageText>[] = [];
			const __itemsCollection: IDictionary<ISelectableListItem<MultiLanguageText>> = {};
			const selectedItemValues: string[] = [];

			data.items.forEach(item => {
				const newItem: ISelectableListItem<MultiLanguageText> = {
					...item,
					text: new MultiLanguageText(),
					selected: item.selected
				};
				newItem.text.setData(item.text);
				__itemsCollection[item.value] = newItem;
				newItems.push(newItem);
				if (newItem.selected && !selectedItemValues.includes(newItem.value)) {
					selectedItemValues.push(newItem.value);
				}
			});

			if (!_isEqual(this.selectedItemValues, selectedItemValues)) {
				this.__proxyChanged.selectedItemValues = selectedItemValues;
			}
			if (!_isEqual(this.__itemsCollection, __itemsCollection)) {
				this.__itemsCollection = __itemsCollection;
			}
			if (!_isEqual(this.items, newItems)) {
				this.__proxyChanged.items = newItems;
			}
		} else {
			this.__proxyChanged.selectedItemValues = data.selectedItemValues;
			this.__proxyChanged.items = [];
			this.__itemsCollection = {};
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			valueType: this.valueType,
			dataSourceType: this.dataSourceType,
			iconExpr: this.iconExpr,
			iconMatcers: this.iconMatchers,
			dataCount: this.dataCount
		};
	}

	__loadValueProperty(value: any) {
		// value de�eri items dan y�netiliyor.
	}

	__loadTextProperty(text: string) {
		// text de�eri items dan y�netiliyor.
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const valueType = properties ? properties.get('valueType') : this.value;

		return [
			...super.__getPropertyFields(),
			{
				name: 'selectedValues',
				type: 'array'
			},
			{
				name: 'value',
				type: convertTypeToFieldType(valueType)
			},
			{
				name: 'valueType',
				type: 'enum',
				enumMembers: ['boolean', 'date', 'decimal', 'integer', 'string'] as PrimitiveType[]
			},
			{
				name: 'items',
				type: 'array',
				isArray: true,
				items: [
					{
						name: 'key',
						type: 'string',
						hiddenOn: ['formula-manager', 'rule-manager']
					},
					{
						name: 'text',
						type: 'string',
						hiddenOn: ['formula-manager', 'rule-manager']
					},
					{
						name: 'value',
						type: 'string',
						hiddenOn: ['formula-manager', 'rule-manager']
					}
				]
			}
		];
	}

	__render(children?: React.ReactNode): JSX.Element {
		return super.__render(children);
	}
}
